import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import api from '../../components/services/apiService';
import { useDebounce } from '../../hooks/useDebounce';
import Pagination from '../../components/Pagination/Pagination';
import { usePermissions } from '../../hooks/usePermissions';

interface DiscoverableDevice {
  hostname: string;
  ipAddress: string;
  isWinRMEnabled: boolean;
  isReachableViaRPC: boolean;
  isAdminShareAccessible: boolean;
  osVersion: string;
  validationStatus: string;
  failureReason: string;
  additionalInfoValueType?: string;
  isAgentInstalled?: boolean; // <-- Added field
}

interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

interface DiscoveredAgentsResponse {
  data: DiscoverableDevice[];
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

interface Props {
  deviceData?: any;
}

const DiscoveredAgents: React.FC<Props> = ({ deviceData }) => {
  const auth = useAuth();
  const { canEdit } = usePermissions();
  const canManageDevices = canEdit('deviceManagement');
  const [discoveredAgents, setDiscoveredAgents] = useState<DiscoverableDevice[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedDevices, setSelectedDevices] = useState<Record<string, DiscoverableDevice>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedFailureReasons, setExpandedFailureReasons] = useState<Set<string>>(new Set());
  const pageSize = 20;
  const { selectedTenantId } = useTenantSelection();
  const { addToast } = useToast();
  const debouncedSearch = useDebounce(searchTerm, 600);

  const fetchDiscoveredAgents = useCallback(
    async (search: string = '') => {
      setLoadingAgents(true);
      try {
        const token = auth.userData?.access_token;
        const tenantId = selectedTenantId;
        const agentId = deviceData?.agentId || localStorage.getItem('agent_id');

        if (!token || !tenantId || !agentId) return;

        const res = await api.get<DiscoveredAgentsResponse>(`${Project.api}agentapi/agent-discoverable-devices/${agentId}`);

        if (res.data.isSuccess) {
          // Client-side filtering if search is provided
          let filteredData = res.data.data;
          if (search.trim()) {
            const searchLower = search.toLowerCase();
            filteredData = filteredData.filter(
              (device) =>
                device.hostname?.toLowerCase().includes(searchLower) ||
                device.ipAddress?.toLowerCase().includes(searchLower) ||
                device.osVersion?.toLowerCase().includes(searchLower) ||
                device.validationStatus?.toLowerCase().includes(searchLower),
            );
          }

          setDiscoveredAgents(filteredData);
          setCurrentPage(1); // Reset to first page on new data
        }
      } catch (error) {
        console.error('Error fetching discovered agents:', error);
        setDiscoveredAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    },
    [auth.userData, deviceData?.agentId, selectedTenantId],
  );

  useEffect(() => {
    fetchDiscoveredAgents(searchTerm);
  }, [fetchDiscoveredAgents, debouncedSearch]);

  useEffect(() => {
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  // Paginated data
  const paginatedAgents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return discoveredAgents.slice(startIndex, endIndex);
  }, [discoveredAgents, currentPage, pageSize]);

  const totalPages = Math.ceil(discoveredAgents.length / pageSize);

  // Check if all current page items are selected
  const isAllCurrentPageSelected = useMemo(() => {
    const selectableDevices = paginatedAgents.filter((d) => !d.isAgentInstalled);
    if (selectableDevices.length === 0) return false;
    return selectableDevices.every((device) => {
      const key = `${device.hostname}-${device.ipAddress}`;
      return !!selectedDevices[key];
    });
  }, [paginatedAgents, selectedDevices]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout) clearTimeout(searchTimeout);

    const timeout = setTimeout(() => {
      fetchDiscoveredAgents(value);
    }, 500);

    setSearchTimeout(timeout);
  };

  const toggleDeviceSelection = (device: DiscoverableDevice) => {
    const key = `${device.hostname}-${device.ipAddress}`;
    setSelectedDevices((prev) => {
      const updated = { ...prev };
      if (updated[key]) {
        delete updated[key];
      } else {
        updated[key] = device;
      }
      return updated;
    });
  };

  const toggleSelectAll = () => {
    if (isAllCurrentPageSelected) {
      // Deselect all items on current page
      setSelectedDevices((prev) => {
        const updated = { ...prev };
        paginatedAgents.forEach((device) => {
          const key = `${device.hostname}-${device.ipAddress}`;
          delete updated[key];
        });
        return updated;
      });
    } else {
      // Select only devices without agent installed
      setSelectedDevices((prev) => {
        const updated = { ...prev };
        paginatedAgents.forEach((device) => {
          if (!device.isAgentInstalled) {
            const key = `${device.hostname}-${device.ipAddress}`;
            updated[key] = device;
          }
        });
        return updated;
      });
    }
  };

  const handleInstallPatchifiAgent = async () => {
    setIsInstalling(true);
    try {
      const tenantId = selectedTenantId;
      const agentId = deviceData?.agentId;

      if (!tenantId || !agentId) {
        addToast('Missing tenant or agent information', 'error');
        return;
      }

      if (Object.keys(selectedDevices).length === 0) {
        addToast('Please select at least one device', 'error');
        return;
      }

      const targetAgentDetails = Object.values(selectedDevices).map((device) => ({
        additionalInfoValueType: device.additionalInfoValueType || 'DiscoverableHost',
        hostname: device.hostname,
        ipAddress: device.ipAddress,
        isWinRMEnabled: device.isWinRMEnabled,
        isReachableViaRPC: device.isReachableViaRPC,
        isAdminShareAccessible: device.isAdminShareAccessible,
        osVersion: device.osVersion,
        validationStatus: device.validationStatus,
        failureReason: device.failureReason,
      }));

      const payload = {
        agentIdList: [agentId],
        targetAgentDetails,
      };

      const response = await api.post(`${Project.api}patchcenterservice/RemediationPlan/${tenantId}/install-patchifi-agent`, payload);

      if (response.data.isSuccess) {
        addToast(response.data.message || 'Patchifi agent installation initiated successfully', 'success');
        setSelectedDevices({});
      } else {
        addToast(response.data.message || 'Failed to initiate installation', 'error');
      }
    } catch (error: any) {
      console.error('Error installing Patchifi agent:', error);
      addToast(error?.response?.data?.message || 'Failed to initiate Patchifi agent installation', 'error');
    } finally {
      setIsInstalling(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const LoadingSkeleton = ({ columnCount }: any) => (
    <>
      {[...Array(10)].map((_, index) => (
        <tr
          key={`skeleton-${index}`}
          className={`${index % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'} animate-slideIn`}
          style={{ animationDelay: `${index * 100}ms`, height: '60px' }}
        >
          {[...Array(columnCount)].map((_, idx) => (
            <td key={idx} className="p-2">
              <div className="animate-pulse bg-zinc-700 h-4 rounded animate-shimmer"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Ready':
        return 'bg-green-900/30 text-green-400';
      case 'Failed':
      case 'Error':
        return 'bg-red-900/30 text-red-400';
      case 'Pending':
        return 'bg-yellow-900/30 text-yellow-400';
      default:
        return 'bg-gray-900/30 text-gray-400';
    }
  };

  const getBooleanBadge = (value: boolean) => {
    return value ? (
      <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded-md text-sm font-medium">Yes</span>
    ) : (
      <span className="px-2 py-1 bg-red-900/30 text-red-400 rounded-md text-sm font-medium">No</span>
    );
  };

  const toggleFailureReason = (key: string) => {
    setExpandedFailureReasons((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const FailureReasonCell = ({ device, deviceKey }: { device: DiscoverableDevice; deviceKey: string }) => {
    const failureReason = device.failureReason;
    if (!failureReason || failureReason === '-') return <span>-</span>;

    const isExpanded = expandedFailureReasons.has(deviceKey);
    const lines = failureReason.split('\n');
    const hasMultipleLines = lines.length > 3 || failureReason.length > 150;

    if (!hasMultipleLines) {
      return <span className="text-sm">{failureReason}</span>;
    }

    const truncatedText = isExpanded ? failureReason : lines.slice(0, 3).join('\n') + (lines.length > 3 ? '...' : '');

    return (
      <div className="space-y-1">
        <p className="text-sm whitespace-pre-wrap break-words">{truncatedText}</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleFailureReason(deviceKey);
          }}
          className="text-xs text-blue-400 hover:text-blue-300 underline transition-colors"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      </div>
    );
  };

  return (
    <div>
      {/* Search and Install Button */}
      <div className="flex justify-between items-center mb-4 ml-4 mr-4">
        <div className="relative w-3/5">
          <input
            type="text"
            placeholder="Search discovered agents..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="bg-zinc-900 text-white px-4 py-2 rounded w-full border border-zinc-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 pr-10"
          />
          {searchTerm && !loadingAgents && (
            <button
              onClick={() => {
                setSearchTerm('');
                fetchDiscoveredAgents('');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
            >
              ✕
            </button>
          )}
        </div>

        {/* Install Patchifi Agent Button */}
        {canManageDevices && (
          <button
            onClick={handleInstallPatchifiAgent}
            disabled={Object.keys(selectedDevices).length === 0 || isInstalling}
            style={{
              backgroundColor: Object.keys(selectedDevices).length > 0 && !isInstalling ? '#B91C1C' : '#555',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: 6,
              border: 'none',
              cursor: Object.keys(selectedDevices).length > 0 && !isInstalling ? 'pointer' : 'not-allowed',
              opacity: Object.keys(selectedDevices).length > 0 && !isInstalling ? 1 : 0.7,
              transition: 'background-color 0.3s ease, opacity 0.3s ease',
            }}
          >
            {isInstalling
              ? 'Installing...'
              : `Install Patchifi Agent${Object.keys(selectedDevices).length > 0 ? ` (${Object.keys(selectedDevices).length})` : ''}`}
          </button>
        )}
      </div>

      {/* Table */}
      <table className="w-full text-sm table-auto border-collapse ml-4">
        <thead className="bg-zinc-900">
          <tr className="text-left">
            <th className="p-2" style={{ width: '50px' }}>
              <div
                onClick={toggleSelectAll}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  border: '2px solid #888',
                  backgroundColor: isAllCurrentPageSelected && paginatedAgents.length > 0 ? '#00FF00' : 'transparent',
                  cursor: 'pointer',
                  display: 'inline-block',
                  position: 'relative',
                }}
                title={isAllCurrentPageSelected ? 'Deselect all on this page' : 'Select all on this page'}
              >
                {isAllCurrentPageSelected && paginatedAgents.length > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', left: '3px', color: '#000', fontSize: '14px', fontWeight: 'bold' }}>
                    ✓
                  </span>
                )}
              </div>
            </th>
            <th className="p-2">Hostname</th>
            <th className="p-2">IP Address</th>
            <th className="p-2">OS Version</th>
            <th className="p-2">Agent Status</th>
            <th className="p-2">WinRM Enabled</th>
            <th className="p-2">RPC Reachable</th>
            <th className="p-2">Admin Share</th>
            <th className="p-2">Validation Status</th>
            <th className="p-2">Failure Reason</th>
          </tr>
        </thead>
        <tbody>
          {loadingAgents ? (
            <LoadingSkeleton columnCount={10} />
          ) : paginatedAgents.length === 0 ? (
            <tr>
              <td colSpan={10} className="p-8 text-center text-gray-400">
                No discovered agents found
              </td>
            </tr>
          ) : (
            paginatedAgents.map((device, i) => {
              const key = `${device.hostname}-${device.ipAddress}`;
              const isSelected = !!selectedDevices[key];

              return (
                <tr key={i} className={i % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'} style={{ height: '60px' }}>
                  <td className="p-2">
                    <div
                      onClick={() => !device.isAgentInstalled && toggleDeviceSelection(device)}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        border: '2px solid #888',
                        backgroundColor: isSelected ? '#00FF00' : 'transparent',
                        cursor: device.isAgentInstalled ? 'not-allowed' : 'pointer',
                        opacity: device.isAgentInstalled ? 0.5 : 1,
                      }}
                      title={device.isAgentInstalled ? 'Agent already installed' : 'Select device'}
                    />
                  </td>
                  <td className="p-2">{device.hostname}</td>
                  <td className="p-2">{device.ipAddress}</td>
                  <td className="p-2">{device.osVersion}</td>
                  <td className="p-2">
                    {device.isAgentInstalled ? (
                      <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded-md text-sm font-medium">Installed</span>
                    ) : (
                      <span className="px-2 py-1 rounded-md text-sm font-medium">Not Installed</span>
                    )}
                  </td>
                  <td className="p-2">{getBooleanBadge(device.isWinRMEnabled)}</td>
                  <td className="p-2">{getBooleanBadge(device.isReachableViaRPC)}</td>
                  <td className="p-2">{getBooleanBadge(device.isAdminShareAccessible)}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 ${getStatusBadgeClass(device.validationStatus)} rounded-md text-sm font-medium`}>
                      {device.validationStatus}
                    </span>
                  </td>
                  <td className="p-2" style={{ maxWidth: '250px' }}>
                    <FailureReasonCell device={device} deviceKey={key} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {!loadingAgents && discoveredAgents.length > 0 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} loading={loadingAgents} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default DiscoveredAgents;
